import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import classNames from 'classnames/bind';
import { connect } from 'react-redux';
import { resultsConfigSelector, sortedAndFilteredProductsSelector, quoteFormSelector } from 'ctm-selectors';
import { List } from 'immutable';
import { nextPage, prevPage, startPage } from '../actions';
import { Icon } from '../../icon/icon';
import styles from './results_pagination.css';

/**
 * If we're on the first page, previous button must be disabled. If we're on the last page next button must be
 * disabled. Also renders the current product range given a range value, total number of products, and page number
 * @param {Object} args - The args
 * @param {Number} args.currentPage - the current results page
 * @param {Number} args.range - how many results per page
 * @param {Number} args.total - the total amount of results
 * @param {Function} args.onNextClick - function to fire on next button click
 * @param {Function} args.onPrevClick - function to fire on prev button click
 * @returns {Node} - the results pagination component
 * @constructor
 */
class _ResultsPagination extends PureComponent {

    /**
     * Reset the page number back to start when re-entering results from a page that
     * is not results or more info.
     */
    componentDidMount = () => {
        const { lastViewedPage, startPageAction } = this.props;
        if (!['results', 'more_info'].includes(lastViewedPage)) {
            startPageAction();
        }
    };

    /**
     * filter quotes by Excess Filter if required
     * @param {Array} quotes - The array of quotes to render within the affixed header
     * @param {Boolean} hideHighExcess - should we filter the excess
     * @param {Integer} excessFilterValue - current excess filter value
     * @returns {Array} of quotes
     */
    getDisplayableQuotes = (quotes, hideHighExcess, excessFilterValue) => {
        if (hideHighExcess) {
            return List(quotes.filter((quote) => (quote.get('productMeta').get('excess') <= excessFilterValue)));
        }
        return quotes;
    };

    render = () => {
        const { pageNumber, range, nextPageAction, prevPageAction, unavailableBrands, quotes, hideHighExcess, excessFilterValue } = this.props;
        const cx = classNames.bind(styles);
        const newQuotes = this.getDisplayableQuotes(quotes, hideHighExcess, excessFilterValue);
        const total = newQuotes.size;
        const totalPerPage = range * pageNumber;
        const isFirstPage = pageNumber === 1;
        const isLastPage = totalPerPage >= (unavailableBrands ? total + 1 : total);
        let startRange = pageNumber === 1 ? pageNumber : totalPerPage - range + 1;
        if (unavailableBrands && isLastPage && (total + 1) % range === 1) {
            startRange -= 1;
        }
        const endRange = totalPerPage > total ? total : totalPerPage;
        return (
            <div className={styles.pagination}>
                <button
                    type="button"
                    className={cx({ button: !isFirstPage, button_disabled: isFirstPage })}
                    disabled={isFirstPage}
                    onClick={() => { prevPageAction(pageNumber); }}
                >
                    <Icon variant="ctm_angle_left" size="xlarge" />
                </button>
                <span className={styles.label}>
                    {`Product ${startRange} to ${endRange} of ${total}`}
                </span>
                <button
                    type="button"
                    className={cx({ button: !isLastPage, button_disabled: isLastPage })}
                    disabled={isLastPage}
                    onClick={() => { nextPageAction(pageNumber); }}
                >
                    <Icon variant="ctm_angle_right" size="xlarge" />
                </button>
            </div>
        );
    };

}

_ResultsPagination.propTypes = {
    pageNumber: PropTypes.number.isRequired,
    range: PropTypes.number.isRequired,
    nextPageAction: PropTypes.func.isRequired,
    prevPageAction: PropTypes.func.isRequired,
    startPageAction: PropTypes.func.isRequired,
    unavailableBrands: PropTypes.bool.isRequired,
    lastViewedPage: PropTypes.string,
    hideHighExcess: PropTypes.bool,
    excessFilterValue: PropTypes.number,
    quotes: PropTypes.object
};

const mapStateToProps = (state) => {
    const form = quoteFormSelector(state);
    const quotes = sortedAndFilteredProductsSelector(state);
    const isCar = state.getIn(['page', 'vertical']) === 'car-insurance';
    const hideHighExcess = isCar && state.getIn(['page', 'isHideHighExcess']);
    const excessFilterValue = parseInt(form.getIn(['coverDetail', 'requestedExcess']), 10);
    return {
        quotes,
        pageNumber: resultsConfigSelector(state).get('pageNumber'),
        lastViewedPage: state.getIn(['page', 'lastViewedPage']),
        hideHighExcess,
        excessFilterValue
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        nextPageAction: (pageNumber) => {
            dispatch(nextPage(pageNumber));
        },
        prevPageAction: (pageNumber) => {
            dispatch(prevPage(pageNumber));
        },
        startPageAction: () => {
            dispatch(startPage());
        }
    };
};

const ResultsPagination = connect(mapStateToProps, mapDispatchToProps)(_ResultsPagination);
export { _ResultsPagination, ResultsPagination };
